import React, { useEffect, useRef, useState } from "react";
import { isMapKitQuotaOrAuthError, loadMapKitJs } from "../services/mapKitService";

const DEFAULT_CENTER = { latitude: 41.0082, longitude: 28.9784 };
const clampZoom = (value) => Math.max(2, Math.min(19, Number(value) || 11));
const spanForZoom = (zoom) => {
  const delta = 360 / (2 ** clampZoom(zoom));
  return { latitudeDelta: delta, longitudeDelta: delta };
};
const zoomForSpan = (span) => clampZoom(Math.log2(360 / Math.max(Number(span?.longitudeDelta) || 360, 0.00001)));
const validCoordinate = (item) =>
  item?.lat !== "" && item?.lng !== "" && item?.lat != null && item?.lng != null &&
  Number.isFinite(Number(item.lat)) && Number.isFinite(Number(item.lng));
const fullName = (member) => `${member?.ad || ""} ${member?.soyad || ""}`.trim();
const initials = (member) => `${member?.ad?.[0] || ""}${member?.soyad?.[0] || ""}`.toUpperCase().slice(0, 3);
const safeColor = (value, fallback = "#6366f1") => /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(String(value || "")) ? value : fallback;
const colorWithAlpha = (value, alpha = "24") => {
  const color = safeColor(value).slice(1);
  const normalized = color.length === 3 ? color.split("").map((part) => `${part}${part}`).join("") : color.slice(0, 6);
  return `#${normalized}${alpha}`;
};

function mapAppearance(mk, type) {
  const types = mk.MapType || mk.Map?.MapTypes || {};
  const schemes = mk.ColorScheme || mk.Map?.ColorSchemes || {};
  if (type === "uydu") return { mapType: types.Hybrid || types.hybrid || "hybrid", colorScheme: schemes.Light || schemes.light || "light" };
  if (type === "koyu") return { mapType: types.Standard || types.standard || "standard", colorScheme: schemes.Dark || schemes.dark || "dark" };
  if (type === "terrain") return { mapType: types.MutedStandard || types.mutedStandard || types.Standard || "mutedStandard", colorScheme: schemes.Light || "light" };
  return { mapType: types.Standard || types.standard || "standard", colorScheme: schemes.Light || schemes.light || "light" };
}

export default function MapKitFamilyMap({
  konumlar = {}, yerler = [], uyeler = [], seciliUyeUid, takipModu = false,
  haritaTuru = "sokak", lang = "tr", onZoomChange, geofencelar = [],
  guvenliAlanlar = [], bulusmaNoktasi = null, sosAlertler = [], onYerTikla,
  mapRef, onFallback, currentUserUid, resolveMemberAvatar, resolvePlaceStyle,
}) {
  const containerRef = useRef(null);
  const kitRef = useRef(null);
  const nativeMapRef = useRef(null);
  const dataAnnotationsRef = useRef([]);
  const dataOverlaysRef = useRef([]);
  const historyItemsRef = useRef({ overlays: [], annotations: [] });
  const selectionRef = useRef(null);
  const fallbackRef = useRef(onFallback);
  const placeClickRef = useRef(onYerTikla);
  const [ready, setReady] = useState(false);

  useEffect(() => { fallbackRef.current = onFallback; }, [onFallback]);
  useEffect(() => { placeClickRef.current = onYerTikla; }, [onYerTikla]);

  useEffect(() => {
    let cancelled = false;
    let configErrorHandler = null;
    let regionHandler = null;
    let annotationSelectHandler = null;
    let retryTimer = null;

    const initializeMapKit = (attempt = 0) => loadMapKitJs(lang).then((mk) => {
      if (cancelled || !containerRef.current) return;
      kitRef.current = mk;
      configErrorHandler = (event) => {
        fallbackRef.current?.(
          isMapKitQuotaOrAuthError(event) || event?.status
            ? "mapkit-configuration"
            : "mapkit-runtime"
        );
      };
      mk.addEventListener?.("error", configErrorHandler);

      const appearance = mapAppearance(mk, haritaTuru);
      const map = new mk.Map(containerRef.current, {
        center: DEFAULT_CENTER,
        region: { center: DEFAULT_CENTER, span: spanForZoom(11) },
        mapType: appearance.mapType,
        colorScheme: appearance.colorScheme,
        showsCompass: mk.FeatureVisibility?.Adaptive || "adaptive",
        showsZoomControl: true,
        showsMapTypeControl: false,
        showsUserLocationControl: false,
      });
      nativeMapRef.current = map;

      annotationSelectHandler = (event) => {
        const place = event?.annotation?.data?.familyPlace;
        if (place) placeClickRef.current?.(place);
      };
      map.addEventListener?.("select", annotationSelectHandler);

      const adapter = {
        _renderer: "mapkit",
        flyTo: ([lat, lng], zoom = 15) => {
          const region = { center: { latitude: Number(lat), longitude: Number(lng) }, span: spanForZoom(zoom) };
          map.setRegionAnimated?.(region, true);
          if (!map.setRegionAnimated) map.region = region;
        },
        getCenter: () => ({ lat: map.center?.latitude, lng: map.center?.longitude }),
        getRegion: () => map.region ? {
          center: {
            latitude: Number(map.region.center?.latitude),
            longitude: Number(map.region.center?.longitude),
          },
          span: {
            latitudeDelta: Number(map.region.span?.latitudeDelta),
            longitudeDelta: Number(map.region.span?.longitudeDelta),
          },
        } : null,
        getZoom: () => zoomForSpan(map.region?.span),
        invalidateSize: () => {},
        remove: () => map.destroy?.(),
        setHistoryRoute: (points, color) => {
          if (historyItemsRef.current.overlays.length) map.removeOverlays?.(historyItemsRef.current.overlays);
          if (historyItemsRef.current.annotations.length) map.removeAnnotations?.(historyItemsRef.current.annotations);
          const coordinates = points.filter(validCoordinate).map((point) => ({ latitude: Number(point.lat), longitude: Number(point.lng) }));
          if (!coordinates.length) return;
          const line = new mk.PolylineOverlay(coordinates, { style: new mk.Style({ strokeColor: safeColor(color), lineWidth: 4, lineJoin: "round", lineCap: "round" }) });
          const start = new mk.MarkerAnnotation(coordinates[0], { color: "#10b981", glyphText: "S", title: lang === "en" ? "Start" : "Başlangıç" });
          const end = coordinates.length > 1 ? new mk.MarkerAnnotation(coordinates[coordinates.length - 1], { color: safeColor(color), glyphText: "●", title: lang === "en" ? "Last position" : "Son konum" }) : null;
          const annotations = [start, end].filter(Boolean);
          historyItemsRef.current = { overlays: [line], annotations };
          map.addOverlay?.(line);
          map.addAnnotations?.(annotations);
          map.showItems?.([line, ...annotations], { padding: { top: 32, right: 32, bottom: 32, left: 32 }, animate: true });
        },
        clearHistoryRoute: () => {
          const items = historyItemsRef.current;
          if (items.overlays.length) map.removeOverlays?.(items.overlays);
          if (items.annotations.length) map.removeAnnotations?.(items.annotations);
          historyItemsRef.current = { overlays: [], annotations: [] };
        },
        setHistorySelection: (lat, lng, color) => {
          if (selectionRef.current) map.removeAnnotation?.(selectionRef.current);
          selectionRef.current = new mk.MarkerAnnotation({ latitude: Number(lat), longitude: Number(lng) }, { color: safeColor(color), glyphText: "●" });
          map.addAnnotation?.(selectionRef.current);
        },
      };
      if (mapRef) mapRef.current = adapter;
      regionHandler = () => onZoomChange?.(adapter.getZoom());
      map.addEventListener?.("region-change-end", regionHandler);
      setReady(true);
    }).catch(() => {
      if (cancelled) return;
      if (attempt === 0) {
        retryTimer = window.setTimeout(() => initializeMapKit(1), 750);
        return;
      }
      fallbackRef.current?.("mapkit-load");
    });

    initializeMapKit();

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      const mk = kitRef.current;
      const map = nativeMapRef.current;
      if (configErrorHandler) mk?.removeEventListener?.("error", configErrorHandler);
      if (regionHandler) map?.removeEventListener?.("region-change-end", regionHandler);
      if (annotationSelectHandler) map?.removeEventListener?.("select", annotationSelectHandler);
      try { map?.destroy?.(); } catch (_) {}
      if (mapRef?.current?._renderer === "mapkit") mapRef.current = null;
      nativeMapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const mk = kitRef.current;
    const map = nativeMapRef.current;
    if (!mk || !map) return;
    const appearance = mapAppearance(mk, haritaTuru);
    map.mapType = appearance.mapType;
    map.colorScheme = appearance.colorScheme;
  }, [haritaTuru, ready]);

  useEffect(() => {
    const mk = kitRef.current;
    const map = nativeMapRef.current;
    if (!mk || !map) return;
    if (dataAnnotationsRef.current.length) map.removeAnnotations?.(dataAnnotationsRef.current);
    if (dataOverlaysRef.current.length) map.removeOverlays?.(dataOverlaysRef.current);
    const annotations = [];
    const overlays = [];

    uyeler.forEach((member, index) => {
      const uid = member?.uid || member?.id;
      const location = konumlar?.[uid];
      if (!location?.paylasimAcik || !validCoordinate(location)) return;
      const color = safeColor(member?.calendarColor || member?.renk, ["#6366f1", "#10b981", "#f59e0b", "#ef4444"][index % 4]);
      const avatar = resolveMemberAvatar?.(member);
      if (avatar && mk.Annotation) {
        const annotation = new mk.Annotation(
          { latitude: Number(location.lat), longitude: Number(location.lng) },
          () => {
            const marker = document.createElement("div");
            marker.className = "haritaUyeMarker haritaMapKitUyeMarker";
            marker.style.setProperty("--harita-uye-renk", color);
            const avatarWrap = document.createElement("div");
            avatarWrap.className = "haritaUyeMarkerAvatar custom";
            const image = document.createElement("img");
            image.src = avatar;
            image.alt = "";
            image.draggable = false;
            avatarWrap.appendChild(image);
            marker.appendChild(avatarWrap);
            return marker;
          },
          { title: fullName(member), subtitle: member?.rol || "" }
        );
        annotations.push(annotation);
      } else {
        annotations.push(new mk.MarkerAnnotation(
          { latitude: Number(location.lat), longitude: Number(location.lng) },
          { title: fullName(member), subtitle: member?.rol || "", color, glyphText: initials(member) || "•" }
        ));
      }
    });

    yerler.forEach((place) => {
      if (!validCoordinate(place)) return;
      const placeStyle = resolvePlaceStyle?.(place) || {};
      const annotation = new mk.MarkerAnnotation(
        { latitude: Number(place.lat), longitude: Number(place.lng) },
        { title: place.ad || "", subtitle: place.adres || "", color: safeColor(placeStyle.renk, "#0ea5e9"), glyphText: placeStyle.ikon || "●" }
      );
      annotation.data = { ...(annotation.data || {}), familyPlace: place };
      annotations.push(annotation);
    });

    const addCircle = (item, color, dashed = false) => {
      if (!validCoordinate(item)) return;
      overlays.push(new mk.CircleOverlay(
        { latitude: Number(item.lat), longitude: Number(item.lng) },
        Number(item.yaricap) || 300,
        { style: new mk.Style({ strokeColor: color, fillColor: colorWithAlpha(color), lineWidth: 2.5, lineDash: dashed ? [8, 5] : [] }), data: item }
      ));
    };
    geofencelar.forEach((item) => addCircle(item, safeColor(item?.renk), true));
    guvenliAlanlar.forEach((item) => addCircle(item, "#10b981", true));

    const meetingList = Array.isArray(bulusmaNoktasi) ? bulusmaNoktasi : (bulusmaNoktasi ? [bulusmaNoktasi] : []);
    meetingList.filter(validCoordinate).forEach((point) => annotations.push(new mk.MarkerAnnotation(
      { latitude: Number(point.lat), longitude: Number(point.lng) },
      { title: lang === "en" ? "Meeting Point" : "Buluşma Noktası", subtitle: point.ad || "", color: "#f59e0b", glyphText: "M" }
    )));
    sosAlertler.filter(validCoordinate).forEach((alert) => annotations.push(new mk.MarkerAnnotation(
      { latitude: Number(alert.lat), longitude: Number(alert.lng) },
      { title: `SOS · ${alert.tamAd || ""}`, subtitle: lang === "en" ? "Emergency Help" : "Acil Yardım", color: "#ef4444", glyphText: "!" }
    )));

    if (annotations.length) map.addAnnotations?.(annotations);
    if (overlays.length) map.addOverlays?.(overlays);
    dataAnnotationsRef.current = annotations;
    dataOverlaysRef.current = overlays;
  }, [konumlar, yerler, uyeler, geofencelar, guvenliAlanlar, bulusmaNoktasi, sosAlertler, lang, ready, resolveMemberAvatar, resolvePlaceStyle]);

  useEffect(() => {
    if (seciliUyeUid) return;
    const location = konumlar?.[currentUserUid];
    if (!validCoordinate(location) || !mapRef?.current) return;
    mapRef.current.flyTo([location.lat, location.lng], 15);
  }, [currentUserUid, konumlar, mapRef, ready, seciliUyeUid]);

  useEffect(() => {
    const location = konumlar?.[seciliUyeUid];
    if (!validCoordinate(location) || !mapRef?.current) return;
    const currentZoom = mapRef.current.getZoom?.() || 15;
    mapRef.current.flyTo([location.lat, location.lng], currentZoom > 10 ? currentZoom : 15);
  }, [seciliUyeUid, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const location = konumlar?.[seciliUyeUid];
    if (!takipModu || !validCoordinate(location) || !mapRef?.current) return;
    mapRef.current.flyTo([location.lat, location.lng], mapRef.current.getZoom?.() || 17);
  }, [konumlar, takipModu, seciliUyeUid, mapRef]);

  return <div ref={containerRef} className="haritaLeafletWrap haritaMapKitWrap" data-map-provider="apple" />;
}
